import {defineMessages, FormattedMessage} from 'react-intl';
import {Skeleton, view, Action, Icon} from '@sail/ui';
import {getStore} from 'src/v2/state/store';
import {WalletCard} from 'src/v2/components/wallet/WalletCard';
import {ErrorBanner} from 'src/v2/components/error/ErrorBanner';
import {Text} from 'src/v2/components/common';
import {COLORS} from 'src/v2/util/color';
import {useInitializeExchangePayment} from 'src/v2/components/exchangeScreen/useInitializeExchangePayment';
import {ExchangeTransactionProcessing} from 'src/v2/components/exchangeScreen/ExchangeTransactionProcessing';
import {cancel, external} from '@sail/icons/react/Icon';
import {QrCode} from 'src/v2/components/common/QrCode';
import {BrandedButton} from 'src/v2/components/common/BrandedButton';
import {safeWindowOpen} from '@stripe-internal/safe-links';
import {isMobile} from 'src/utils/utils';

const messages = defineMessages({
  title: {
    id: 'crypto.payins.v2.exchange_screen.title',
    description: 'Title for exchange screen',
    defaultMessage: 'Pay with Crypto.com',
  },
  description: {
    id: 'crypto.payins.v2.exchange_screen.description',
    description: 'Description for Crypto.com',
    defaultMessage:
      'Scan this QR code from your Crypto.com app to process your payment.',
  },
  testModeMessage: {
    id: 'crypto.payins.v2.exchange_screen.test_mode_message',
    description: 'Message shown when QR code is not available in test mode',
    defaultMessage: 'QR code is not available in testmode',
  },
  continueToPayment: {
    id: 'crypto.payins.v2.exchange_screen.continue_to_payment',
    description: 'Button text to continue to payment in test mode',
    defaultMessage: 'Continue to payment',
  },
  connectButton: {
    id: 'crypto.payins.v2.exchange_screen.connect_button',
    description: 'Button text to pay with exchange on mobile',
    defaultMessage: 'Pay with Crypto.com',
  },
});

const QR_CODE_SIZE = 266;

// False positive: the onclick is synchronously opening a new window.
// nosemgrep: no-async-window-open
export const ExchangeScreen = () => {
  const store = getStore();

  const {paymentUrl, paymentQrCode} = useInitializeExchangePayment();

  const exchangeOption = store.useSelector((state) => state.exchangeOption);
  const livemode = store.useSelector((state) => state.livemode);
  const sessionState = store.useSelector((state) => state.state);
  const isMobileDevice = isMobile();
  const mobileDeeplink = `monaco://pay?payment_url=${paymentQrCode}`;

  if (!exchangeOption) {
    throw new Error('exchangeOption is required');
  }

  const {name, iconUrl} = exchangeOption;
  // It takes some time for the redirect to complete when the session becomes 'fulfillment_complete',
  // so we continue show the processing state until then.
  const isTransactionProcessing =
    sessionState === 'purchase_complete' ||
    sessionState === 'fulfillment_complete';

  if (isTransactionProcessing) {
    return (
      <>
        <ExchangeTransactionProcessing />
        <ErrorBanner />
      </>
    );
  }

  return (
    <>
      <WalletCard
        wallet={{
          option: exchangeOption,
        }}
        bottomContent={
          isMobileDevice ? (
            <view.div
              css={{
                stack: 'y',
                alignX: 'center',
                alignY: 'center',
                gap: 'medium',
              }}
            >
              {mobileDeeplink ? (
                <BrandedButton
                  onClick={() => {
                    safeWindowOpen(
                      mobileDeeplink,
                      '_self',
                      {},
                      {allowedProtocols: ['monaco:']},
                    );
                  }}
                  css={{
                    borderRadius: '8px',
                    width: 'fill',
                  }}
                >
                  <view.div
                    css={{
                      stack: 'x',
                      alignX: 'center',
                      alignY: 'center',
                      gap: 'xsmall',
                    }}
                  >
                    <FormattedMessage {...messages.connectButton} />
                    <Icon icon={external} size="xsmall" />
                  </view.div>
                </BrandedButton>
              ) : (
                <Skeleton css={{width: 'fill', height: 'space.500'}} />
              )}
            </view.div>
          ) : (
            <view.div
              css={{
                stack: 'y',
                alignX: 'center',
                alignY: 'center',
                padding: '24px 12px',
                gap: 'medium',
              }}
            >
              <view.div
                css={{
                  stack: 'y',
                  gap: 'xsmall',
                  width: QR_CODE_SIZE,
                  alignX: 'center',
                }}
              >
                <Text type="heading" css={{color: COLORS.textPrimary}}>
                  <FormattedMessage
                    {...messages.title}
                    values={{provider: name}}
                  />
                </Text>
                <Text type="body" css={{textAlign: 'center'}}>
                  <FormattedMessage
                    {...messages.description}
                    values={{provider: name}}
                  />
                </Text>
              </view.div>
              {paymentUrl && paymentQrCode ? (
                <view.div css={{stack: 'z'}}>
                  <view.div
                    css={
                      !livemode
                        ? {
                            filter: 'blur(6px)',
                            opacity: 0.24,
                          }
                        : undefined
                    }
                  >
                    <QrCode
                      qrCodeUri={paymentQrCode}
                      iconUrl={iconUrl}
                      name={name}
                    />
                  </view.div>
                  {/* Crypto.com does not support payments in app in test mode */}
                  {!livemode ? (
                    <view.div
                      css={{
                        alignSelfX: 'center',
                        alignSelfY: 'center',
                        stack: 'y',
                        gap: 'small',
                        alignX: 'center',
                        paddingX: 'medium',
                        zIndex: 99,
                      }}
                    >
                      <Text
                        type="caption"
                        css={{
                          color: COLORS.textPrimary,
                          textAlign: 'center',
                          width: '292px',
                        }}
                      >
                        <FormattedMessage {...messages.testModeMessage} />
                      </Text>
                      <BrandedButton
                        onClick={() => {
                          safeWindowOpen(paymentUrl, '_blank');
                        }}
                        css={{
                          padding: '8px 12px',
                          borderRadius: '8px',
                          height: '32px',
                          width: '145px',
                        }}
                        textType="caption"
                        textCss={{fontWeight: '510'}}
                      >
                        <FormattedMessage {...messages.continueToPayment} />
                      </BrandedButton>
                    </view.div>
                  ) : null}
                </view.div>
              ) : (
                <Skeleton css={{width: '266px', height: '266px'}} />
              )}
            </view.div>
          )
        }
        headerActions={
          <Action
            onPress={() => {
              store.setValue((state) => {
                state.screen = 'WALLET_SELECT_GRID';
              });
            }}
          >
            <Icon icon={cancel} size="xsmall" />
          </Action>
        }
      />
      <ErrorBanner />
    </>
  );
};
